import { Injectable } from '@angular/core';
import { Headers } from 'app/services/http';
import { AuthService } from './authentication/auth.service';
import { LoggerService } from 'app/logger/logger.service';
import { AUTHORIZATION, CONTENT_TYPE, GlobalVariables } from 'app/services/globals';
import { HttpErrorResponse } from '@angular/common/http';

import { convertStringToObject } from 'app/shared/utils/string.util';
import { UrlInformationBaseService } from './url-information-base.service';
import { AuthConstants } from './authentication/auth-constants';
import { IStringMap } from 'app/interfaces/IStringMap';
import CryptoJS from 'crypto-js';

@Injectable()
export abstract class HttpClientServiceBase {
    protected static className = 'HttpClientServiceBase';
    protected authArea: string = AuthConstants.AUTH_AREA;

    public constructor(
        protected readonly authService: AuthService,
        protected readonly urlService: UrlInformationBaseService,
        protected readonly logger: LoggerService) {
    }

    public getBaseUri(): string {
        return `${AuthConstants.RESOURCE_MANAGER_ENDPOINT}${this.urlService.getResourceId()}`;
    }

    public getAzureBaseUri(): string {
        return `${AuthConstants.RESOURCE_MANAGER_ENDPOINT}${this.urlService.getAzureBase()}`;
    }

    public getBaseResourceUri(): string {
        return `${AuthConstants.RESOURCE_MANAGER_ENDPOINT}${this.urlService.getBaseResource()}`;
    }

    public getSubIdUri(): string {
        return `${AuthConstants.RESOURCE_MANAGER_ENDPOINT}${this.urlService.getSubId()}`;
    }

    protected addEtag(headers: Headers): void {
        // todo: use correct etag.
        // Because currently when pipeline is updated the used shared linkedservice is also updated, which is not expected.
        // Before figure out the scenario, let's always use star for now
        headers.append('If-Match', '*');
    }

    protected async getDefaultHeadersAsync(resourceId?: string, noEtag?: boolean): Promise<Headers> {
        const headers = new Headers();
        const token = await this.authService.getToken(resourceId || this.authArea);
        headers.append(AUTHORIZATION, `Bearer ${token}`);
        headers.append(CONTENT_TYPE, 'application/json');

        if (!noEtag) {
            this.addEtag(headers);
        }

        return headers;
    }

    protected async getSQLHeadersAsync(sqlToken: string, csrfToken: string): Promise<Headers> {
        const headers = new Headers();
        headers.append(AUTHORIZATION, `Bearer ${sqlToken}`);
        headers.append('x-csrf-signature', csrfToken);
        return headers;
    }

    protected getDefaultParams(params?: object, apiVersion: string = GlobalVariables.dataFactoryVersion): {} {
        if (params) {
            const newParams = JSON.parse(JSON.stringify(params));
            newParams['api-version'] = apiVersion;
            return newParams;
        } else {
            return {
                'api-version': apiVersion
            };
        }
    }

    protected parseError(error: HttpErrorResponse): string {
        let errorMessage = stringResources.Unknown;
        if (error && error.error) {
            const body = convertStringToObject(error.error);
            if (body && body.error && body.error.message) {
                errorMessage = body.error.message;
            } else {
                this.logger.error(HttpClientServiceBase.className, `Could not parse error object: ${error.error}`);
            }
        }
        return errorMessage;
    }

    protected signHeaders(headers: IStringMap<string>): IStringMap<string> {
        const maxClockSkewMS = 5 * 60 * 1000;
        const notBefore = new Date();
        notBefore.setMilliseconds(notBefore.getMilliseconds() - maxClockSkewMS);
        const notAfter = new Date();
        notAfter.setMilliseconds(notAfter.getMilliseconds() + maxClockSkewMS);

        const notBeforeValue = `not-before=${notBefore.toISOString().replace(/\.[0-9]+Z$/, 'Z')}`;
        const notAfterValue = `not-after=${notAfter.toISOString().replace(/\.[0-9]+Z$/, 'Z')}`;

        let signedHeaders = '';
        let signed = `${notBeforeValue}\r\n${notAfterValue}\r\n`;

        const canonicalizedHeaders: IStringMap<string> = {};
        for (const header of Object.keys(headers)) {
            canonicalizedHeaders[header.toLowerCase().trim()] = headers[header];
        }

        for (const header of Object.keys(canonicalizedHeaders).sort()) {
            const value = canonicalizedHeaders[header];
            signedHeaders += (signedHeaders ? ',' : '') + header;
            signed += `${header}: ${value}\r\n`;
        }

        const hash = CryptoJS.HmacSHA256(signed, canonicalizedHeaders.authorization);
        const hashInBase64 = CryptoJS.enc.Base64.stringify(hash);
        const signature = hashInBase64;

        headers['X-CSRF-Signature'] = `${signature}; ${notBeforeValue}; ${notAfterValue}; signed-headers=${signedHeaders}`;
        return headers;
    }
}
